import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ColDef } from 'ag-grid-community';
import { goodsSales, DOC_KIND_UA, type DocKind, type GoodsSaleListItem } from '@/api/goodsSales';
import { contractors as contractorsApi } from '@/api/catalogs';
import ImportedBadge from '@/shared/components/ImportedBadge';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import { useSignControlInfinite } from '@/shared/hooks/useSignControl';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import DocListFilters, { ContractorFilter, DocListFiltersRow, NomenclatureFilter, useDocListFilters } from '@/shared/components/DocListFilters';
import { agOrgCol, useOrgNames } from '@/shared/hooks/useOrgNames';

export default function GoodsSalesPage() {
  const orgNames = useOrgNames();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  // Стандартні фільтри списків (еталон /employees): орг + статус + дати.
  const f = useDocListFilters();
  // 2026-07-31 (рішення власника): пошуковий фільтр за покупцем.
  const [contractorFilter, setContractorFilter] = useState('');
  const [nomFilter, setNomFilter] = useState('');
  // 0445: фільтр «Вид» (товари/послуги/обладнання/ОЗ); /goods-sales?doc_kind=FIXED_ASSET
  // — колишній список «Продаж ОЗ».
  const [sp] = useSearchParams();
  const [docKind, setDocKind] = useState<DocKind | ''>((sp.get('doc_kind') as DocKind | null) ?? '');
  // 2026-08-09 (Норма #29): серверна пагінація + сортування (канон
  // номенклатури) — limit 200 ховав хвіст списку.
  const { datasource, total } = useInfiniteGridDatasource<GoodsSaleListItem>(
    (limit, offset, sort) => goodsSales.list({
      limit, offset,
      sort_by: sort?.sort_by, sort_dir: sort?.sort_dir,
      organization_id: f.orgId || undefined,
      contractor_id: contractorFilter || undefined,
      nomenclature_id: nomFilter || undefined,
      doc_kind: docKind || undefined,
      date_from: f.dateFrom, date_to: f.dateTo,
    }),
    [contractorFilter, nomFilter, docKind, ...f.queryKey],
    { attachmentsType: 'doc.goods_sales' },
  );
  const contractorsQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.listAll(),
  });

  // Контроль підписання (0429): колонка «Підпис» + фон непідписаних.
  const sc = useSignControlInfinite<GoodsSaleListItem>('doc.goods_sales', f.orgId);

  const contractorName = useMemo(() => {
    const map = new Map<string, string>();
    contractorsQ.data?.items.forEach((c) => map.set(c.id, c.name));
    return map;
  }, [contractorsQ.data]);

  const cols = useMemo<ColDef<GoodsSaleListItem>[]>(() => [
    {
      headerName: '', width: 44, sortable: false,
      cellRenderer: (p: { data?: GoodsSaleListItem }) =>
        <ImportedBadge source={p.data?.external_source} />,
    },
    { field: 'document_number', headerName: t('gs.number'), width: 150, sortable: true },
    {
      field: 'document_date', headerName: t('gs.date'), width: 170, sortable: true,
      valueFormatter: (p) => p.value ? new Date(p.value).toLocaleString(i18n.language) : '',
    },
    agOrgCol(orgNames),
    {
      field: 'contractor_id', headerName: t('gs.contractor'), flex: 1, minWidth: 220, sortable: true,
      valueFormatter: (p) => contractorName.get(p.value) ?? '',
    },
    {
      field: 'doc_kind', headerName: 'Вид', width: 110, sortable: false,
      valueFormatter: (p) => p.value ? (DOC_KIND_UA[p.value as DocKind] ?? '') : '',
    },
    {
      field: 'total_amount', headerName: t('gs.total'), width: 140, sortable: true,
      cellStyle: { textAlign: 'right' },
      valueFormatter: (p) => p.value != null
        ? Number(p.value).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '',
    },
    {
      field: 'currency_code', headerName: 'Валюта', width: 90, sortable: false,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: GoodsSaleListItem }) =>
        p.data?.currency_code && p.data.currency_code !== 'UAH'
          ? <span style={{ color: 'var(--badge-blue-fg)', fontWeight: 700 }}>{p.data.currency_code}</span>
          : (p.data ? 'UAH' : ''),
    },
    {
      colId: '_att', headerName: 'Вкладення', width: 110, sortable: false,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { _att?: boolean } }) =>
        p.data?._att ? '✓' : '',
    },
    {
      field: 'posted', headerName: t('gs.posted'), width: 130, sortable: true,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { posted?: boolean } }) =>
        p.data?.posted ? <span style={{ color: 'var(--badge-green-fg)', fontWeight: 600 }}>✓</span> : '',
    },
  ], [t, i18n.language, contractorName]);

  return (
    <div className="card">
      <div className="toolbar" style={{ gap: '0.4rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>{t('gs.title')}<TitleTotal total={total} /></h2>
        <button className="primary" style={{ marginLeft: 'auto' }} onClick={() => navigate('/goods-sales/new')}>
          + {t('gs.new')}
        </button>
      </div>
      <DocListFiltersRow>
        {/* 2026-08-29 (рішення власника): фільтр статусу прибрано — проведено видно колонкою. */}
        <DocListFilters f={f} />
        <ContractorFilter value={contractorFilter} onChange={setContractorFilter} />
        <NomenclatureFilter value={nomFilter} onChange={setNomFilter} />
        <select value={docKind} onChange={(e) => setDocKind(e.target.value as DocKind | '')}
          title="Вид документа за рядками" style={{ width: 150, flex: '0 0 auto' }}>
          <option value="">Усі види</option>
          {(Object.keys(DOC_KIND_UA) as DocKind[]).map((k) => <option key={k} value={k}>{DOC_KIND_UA[k]}</option>)}
        </select>
      </DocListFiltersRow>
      <InfiniteDocGrid<GoodsSaleListItem>
        copyRow={(r) => navigate(`/goods-sales/new?copy=${r.id}`)}
        deleteRow={{ remove: (id) => goodsSales.remove(id) }}
        datasource={sc.wrap(datasource)}
        columnDefs={[...cols, ...sc.column]}
        storageKey="goods-sales"
        onRowDoubleClicked={(r) => navigate(`/goods-sales/${r.id}`)}
        getRowStyle={(p) => p.data?.deletion_mark
          ? { opacity: 0.4 } : sc.getRowStyle(p)}
      />
    </div>
  );
}
